import { connectivity } from "./connectivity";
import { writeActivity } from "./writeActivity";
import {
  dropPrivate,
  dropPrivateHousehold,
  isCurrentPrivate,
  matchPrivate,
  privateAccountEpoch,
  privateGeneration,
  putPrivate,
  retainPrivateHouseholds,
  scopeOf,
} from "./privateCache";

export interface ErrorDetail {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ErrorDetail[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Thrown when a write is attempted (or a read cannot be served) without a connection. */
export class OfflineError extends Error {
  constructor(message = "You're offline. Reconnect and try again.") {
    super(message);
    this.name = "OfflineError";
  }
}

interface AuthBridge {
  getToken(forceRefresh?: boolean): Promise<string | null>;
  getUid(): string | null;
  onSessionExpired(): void;
  onEmailNotVerified(): void;
}

let bridge: AuthBridge = {
  getToken: async () => null,
  getUid: () => null,
  onSessionExpired: () => undefined,
  onEmailNotVerified: () => undefined,
};

export function configureApi(next: AuthBridge) {
  bridge = next;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  /** Raw body (FormData/Blob) — sent as-is without JSON encoding. */
  rawBody?: BodyInit;
  /** Skip the private offline cache for this GET (e.g. dev outbox, previews). */
  noCache?: boolean;
}

async function send(path: string, opts: RequestOptions, forceRefresh: boolean): Promise<Response> {
  const token = await bridge.getToken(forceRefresh);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  let body: BodyInit | undefined;
  if (opts.rawBody !== undefined) body = opts.rawBody;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  return fetch(path, { method: opts.method ?? "GET", headers, body, signal: opts.signal, cache: "no-store" });
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = "internal";
  let message = `Request failed (${res.status})`;
  let details: ErrorDetail[] = [];
  try {
    const payload = (await res.json()) as { error?: { code?: string; message?: string; details?: ErrorDetail[] } };
    if (payload?.error) {
      code = payload.error.code ?? code;
      message = payload.error.message ?? message;
      details = payload.error.details ?? [];
    }
  } catch {
    // non-JSON error body
  }
  if (res.status >= 500) message = "Supperkeep had a problem on its side. Try again in a moment.";
  return new ApiError(res.status, code, message, details);
}

function isAbort(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

/** A GET that started under a different signed-in account must not reach the UI. */
const staleSession = () => new DOMException("The signed-in account changed while this was loading", "AbortError");

const HOUSEHOLD_ROOT = /^\/api\/v1\/households\/[^/?#]+\/?(?:[?#]|$)/;

/**
 * The server answered that a cached resource is gone or no longer visible to this user. Drop it so a
 * later offline read cannot serve it. A 404 on the household itself means the whole household is gone
 * or membership was removed. Temporary failures (5xx, network) keep the cache.
 */
function forgetDenied(uid: string, path: string, err: ApiError) {
  if (err.code === "email_not_verified") return;
  if (err.status !== 403 && err.status !== 404 && err.status !== 410) return;
  const scope = scopeOf(path);
  if (err.status === 404 && scope !== "account" && HOUSEHOLD_ROOT.test(path)) void dropPrivateHousehold(uid, scope);
  else void dropPrivate(uid, path);
}

/** An online /me lists the user's current households; caches of households they left are deleted. */
async function retainFromMe(uid: string, gen: number, res: Response) {
  try {
    const me = (await res.json()) as { households?: Array<{ id: string }> };
    if (Array.isArray(me?.households)) await retainPrivateHouseholds(uid, me.households.map((h) => h.id), gen);
  } catch {
    // not the expected shape; nothing to prune
  }
}

/**
 * Authenticated fetch. On 401 it refreshes the Firebase ID token once and retries; if that still
 * fails the session is treated as expired. JSON GETs are mirrored into the private offline cache
 * and served from it when the network is unavailable.
 */
async function requestCore(path: string, opts: RequestOptions = {}): Promise<Response> {
  const method = opts.method ?? "GET";
  const uid = bridge.getUid();
  // Captured before any await: cache work and results are bound to the session that started the request.
  const gen = privateGeneration();
  const epoch = privateAccountEpoch();
  const cacheable = method === "GET" && !opts.noCache && uid !== null;
  const stale = () => method === "GET" && privateAccountEpoch() !== epoch;

  if (method !== "GET" && !connectivity.get().browserOnline) throw new OfflineError();

  let res: Response;
  try {
    res = await send(path, opts, false);
    if (res.status === 401 && uid) {
      res = await send(path, opts, true);
      if (res.status === 401) {
        bridge.onSessionExpired();
        throw await toApiError(res);
      }
    }
  } catch (error) {
    if (isAbort(error) || error instanceof ApiError) throw error;
    connectivity.reportNetworkFailure();
    if (cacheable) {
      const cached = await matchPrivate(uid, path, gen);
      if (cached) {
        connectivity.reportServedFromCache();
        return cached;
      }
    }
    throw new OfflineError(method === "GET" ? "You're offline and this hasn't been saved on this device yet." : undefined);
  }
  connectivity.reportServerReached();
  if (stale()) throw staleSession();

  if (!res.ok) {
    const err = await toApiError(res);
    if (err.status === 403 && err.code === "email_not_verified") bridge.onEmailNotVerified();
    if (method === "GET" && uid !== null && isCurrentPrivate(gen, uid)) forgetDenied(uid, path, err);
    throw err;
  }
  if (cacheable && res.headers.get("content-type")?.includes("application/json")) {
    void putPrivate(uid, path, res.clone(), gen);
    if (/^\/api\/v1\/me\/?(?:[?#]|$)/.test(path)) void retainFromMe(uid, gen, res.clone());
  }
  return res;
}

/** Keep background household reads out of the entire write/response-parsing lifetime. */
async function tracked<T>(opts: RequestOptions, work: () => Promise<T>): Promise<T> {
  const finish = opts.method && opts.method !== "GET" ? writeActivity.begin() : undefined;
  try { return await work(); }
  finally { finish?.(); }
}

export function request(path: string, opts: RequestOptions = {}): Promise<Response> {
  return tracked(opts, () => requestCore(path, opts));
}

export function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return tracked(opts, async () => {
    const res = await requestCore(path, opts);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  });
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: "POST", body: body ?? {} });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T = void>(path: string) => api<T>(path, { method: "DELETE" });

/** Build a query string from defined, non-empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const hpath = (householdId: string, rest = "") => `/api/v1/households/${householdId}${rest}`;

/** Human message for any thrown error (never raw internals). */
export function errorMessage(error: unknown, fallback = "Something went wrong. Try again."): string {
  if (error instanceof ApiError) return error.message || fallback;
  if (error instanceof OfflineError) return error.message;
  return fallback;
}

/** Map server `details[].path` to a flat field → message record. */
export function fieldErrors(error: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (error instanceof ApiError) {
    for (const detail of error.details) {
      if (detail.path && !out[detail.path]) out[detail.path] = detail.message;
    }
  }
  return out;
}

/** Fetch a private binary resource (photo, export) as a Blob with the bearer token. */
export async function fetchBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const res = await request(path, { signal, noCache: true });
  return res.blob();
}

/** Current bearer token for non-fetch transports (XHR uploads with progress). */
export const getAuthToken = (forceRefresh = false) => bridge.getToken(forceRefresh);
export const notifySessionExpired = () => bridge.onSessionExpired();
